import { MealTime } from "@/clients/models/MealTime";
import { env } from "@/constants/env";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import { Capacitor } from "@capacitor/core";
import mixpanel from "mixpanel-browser";

export type AnalyticsRuntimeState = "pending" | "disabled" | "initialising" | "ready" | "failed";
let state: AnalyticsRuntimeState = "pending";
let initialisation: Promise<void> | undefined;
let pendingUserId: string | undefined;
let appliedUserId: string | undefined;
let pendingEmail: string | undefined;
let appliedEmail: string | undefined;
let resetRequested = false;

export const getMixpanelState = () => state;

const context = () => ({
  platform: Capacitor.getPlatform(),
  is_native: Capacitor.isNativePlatform(),
  environment: env.VITE_ENVIRONMENT,
  app_version: __APP_VERSION__,
  build_id: __BUILD_ID__,
  analytics_schema_version: 1,
  analytics_policy_version: "product_analytics_v2",
});

const safely = (operation: () => void) => {
  try {
    operation();
  } catch {
    // Never include event payloads or identities in diagnostics.
    state = "failed";
    console.warn("[Mixpanel] Analytics operation failed");
  }
};

const applyIdentity = () => {
  const persistedUserId = mixpanel.get_property("$user_id");
  const previousUserId = appliedUserId ?? persistedUserId;
  if (
    (resetRequested && previousUserId) ||
    (pendingUserId && previousUserId && previousUserId !== pendingUserId)
  ) {
    mixpanel.reset();
    mixpanel.register(context());
    appliedUserId = undefined;
    appliedEmail = undefined;
  }
  resetRequested = false;
  if (!pendingUserId) return;
  const identityChanged = appliedUserId !== pendingUserId;
  if (identityChanged) mixpanel.identify(pendingUserId);
  if (identityChanged || (pendingEmail !== undefined && pendingEmail !== appliedEmail)) {
    mixpanel.people.set({
      platform: Capacitor.getPlatform(),
      ...(pendingEmail !== undefined ? { $email: pendingEmail } : {}),
    });
    appliedEmail = pendingEmail;
  }
  if (identityChanged) {
    mixpanel.people.set_once({
      first_seen: new Date().toISOString(),
      signup_platform: Capacitor.getPlatform(),
    });
  }
  appliedUserId = pendingUserId;
};

/** Permission is resolved before calling this; denied/pending activity is never queued. */
export const initMixpanel = (isEnabled: boolean, replayEnabled = isEnabled): Promise<void> => {
  if (initialisation) return initialisation;
  if (!isEnabled) {
    state = "disabled";
    return (initialisation = Promise.resolve());
  }
  state = "initialising";
  // Defer work so concurrent callers share the promise even if loaded is synchronous.
  initialisation = Promise.resolve().then(
    () =>
      new Promise<void>((resolve) => {
        safely(() => {
          mixpanel.init(env.VITE_MIXPANEL_TOKEN, {
            persistence: "localStorage",
            track_pageview: "url-with-path",
            autocapture: true,
            record_sessions_percent: replayEnabled ? 100 : 0,
            record_mask_all_text: true,
            record_mask_all_inputs: true,
            ip: true,
            api_host: "https://api-eu.mixpanel.com",
            loaded: () => {
              safely(() => {
                mixpanel.register(context());
                applyIdentity();
                state = "ready";
              });
              resolve();
            },
          });
        });
        if (state === "failed") resolve();
      }),
  );
  return initialisation;
};

// Auth reconciliation may supply only an ID; retain email for that same account.
export const setMixpanelUser = (userId: string, email?: string) => {
  if (pendingUserId !== userId) pendingEmail = undefined;
  if (email !== undefined) pendingEmail = email;
  pendingUserId = userId;
  if (state === "ready") safely(applyIdentity);
};

export const resetMixpanel = () => {
  pendingUserId = undefined;
  pendingEmail = undefined;
  resetRequested = true;
  if (state === "ready") safely(applyIdentity);
};

export const trackMixpanelEvent = (
  event: string,
  properties?: Record<string, string | number | boolean>,
) => {
  if (state !== "ready") return;
  safely(() => mixpanel.track(event, { ...properties, ...context() }));
};

export const trackOnboardingStepSubmitted = (stepPath: string) => {
  trackMixpanelEvent(MixpanelEvents.ONBOARDING_STEP_SUBMITTED, {
    step_name: stepPath,
  });
};

export const trackOnboardingCompleted = () => {
  trackMixpanelEvent(MixpanelEvents.ONBOARDING_COMPLETED);
};

export const trackMealTabNavigated = (mealTime: MealTime) => {
  trackMixpanelEvent(MixpanelEvents.MEAL_TAB_NAVIGATED, {
    meal_time: mealTime,
  });
};
